import {
  createContext, useContext, useState, useEffect, useCallback, useMemo,
} from 'react';

const KEY = 'mw-quote-cart-v1';
const QuoteContext = createContext(null);

// Category-based unit defaults
const UNIT_DEFAULT_BY_CATEGORY = {
  pies: 'units',
  burgers: 'units',
};

function inferUnit(product) {
  const c = (product?.category || '').toLowerCase();
  return UNIT_DEFAULT_BY_CATEGORY[c] || 'kg';
}

export function QuoteProvider({ children }) {
  const [items, setItems] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  // Hydrate from localStorage (runs once)
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setItems(parsed);
      }
    } catch {
      /* ignore */
    }
    setHydrated(true);
  }, []);

  // Persist — only AFTER hydration completes, so we never overwrite storage with the empty initial value
  useEffect(() => {
    if (!hydrated) return;
    try {
      window.localStorage.setItem(KEY, JSON.stringify(items));
    } catch {
      /* ignore */
    }
  }, [items, hydrated]);

  const add = useCallback((product, qty = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.product_id === product.id);
      if (existing) {
        return prev.map((i) =>
          i.product_id === product.id ? { ...i, quantity: i.quantity + qty } : i
        );
      }
      return [
        ...prev,
        {
          product_id: product.id,
          product_name: product.name,
          category: product.category || '',
          quantity: qty,
          unit: inferUnit(product),
        },
      ];
    });
    setDrawerOpen(true);
  }, []);

  const remove = useCallback((product_id) => {
    setItems((prev) => prev.filter((i) => i.product_id !== product_id));
  }, []);

  const setQuantity = useCallback((product_id, quantity) => {
    setItems((prev) =>
      prev.map((i) =>
        i.product_id === product_id ? { ...i, quantity: Math.max(0.1, Number(quantity) || 0) } : i
      )
    );
  }, []);

  const setUnit = useCallback((product_id, unit) => {
    setItems((prev) =>
      prev.map((i) => (i.product_id === product_id ? { ...i, unit } : i))
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const openDrawer = useCallback(() => {
    window.dispatchEvent(new CustomEvent('mw-close-cart-drawer'));
    setDrawerOpen(true);
  }, []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  // Listen for open events from other drawers to close ourselves
  useEffect(() => {
    const h = () => setDrawerOpen(false);
    window.addEventListener('mw-close-quote-drawer', h);
    return () => window.removeEventListener('mw-close-quote-drawer', h);
  }, []);

  const count = useMemo(() => items.length, [items]);
  const hasItem = useCallback((product_id) => items.some((i) => i.product_id === product_id), [items]);

  const value = useMemo(
    () => ({
      items, count, hasItem, drawerOpen,
      add, remove, setQuantity, setUnit, clear, openDrawer, closeDrawer,
    }),
    [items, count, hasItem, drawerOpen, add, remove, setQuantity, setUnit, clear, openDrawer, closeDrawer]
  );

  return <QuoteContext.Provider value={value}>{children}</QuoteContext.Provider>;
}

export const useQuote = () => {
  const ctx = useContext(QuoteContext);
  if (!ctx) throw new Error('useQuote must be used inside <QuoteProvider>');
  return ctx;
};
